import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    Modal,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Tooltip,
    LinearProgress,
    useTheme,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import EditIcon from '@mui/icons-material/Edit';

// Adjust these endpoints as needed:
const API_CATEGORIES = 'https://figazuresql.swancreate.com/api/swa/FINANCE_Categories';
const API_SUBCATEGORIES = 'https://figazuresql.swancreate.com/api/swa/FINANCE_Subcategories';
const API_VIEW = 'https://figazuresql.swancreate.com/api/swa/vw_BudgetWorksheetTotals';

const CategoryManager = () => {
    const theme = useTheme();
    const {
        palette: {
            success: { main: successMain },
            background: { default: bgDefault, paper: bgPaper },
            text: { primary: textPrimary },
            action: { hover: actionHover }
        },
    } = theme;

    // Color adjustments based on the theme
    const tableBackground = theme.palette.mode === 'dark'
        ? 'rgba(26, 26, 46, 0.8)'
        : 'rgba(255, 255, 255, 0.8)';
    const headerBackground = theme.palette.mode === 'dark' ? '#16213E' : '#F5F5F5';

    /* ------------------------------------------------------------------ */
    /* ------------------------------ STATE ----------------------------- */
    /* ------------------------------------------------------------------ */
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);

    const [progressOpen, setProgressOpen] = useState(false);
    const [progressCount, setProgressCount] = useState(0);
    const [progressTotal, setProgressTotal] = useState(0);

    // Add-modal flags
    const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
    const [showAddSubcatModal, setShowAddSubcatModal] = useState(false);

    // Add Category fields
    const [newCatName, setNewCatName] = useState('');
    const [newCatDescription, setNewCatDescription] = useState('');
    const [newCatType, setNewCatType] = useState('Expense');
    const [newCatBudget, setNewCatBudget] = useState('');

    // Add Subcategory fields
    const [newSubcatName, setNewSubcatName] = useState('');
    const [newSubcatCategoryId, setNewSubcatCategoryId] = useState('');
    const [newSubcatType, setNewSubcatType] = useState('Expense');
    const [newSubcatBudget, setNewSubcatBudget] = useState('');

    // Edit modals
    const [editingCategory, setEditingCategory] = useState(null);
    const [editingSubcategory, setEditingSubcategory] = useState(null);

    /* ------------------------------------------------------------------ */
    /* -------------------------- DATA FETCH ---------------------------- */
    /* ------------------------------------------------------------------ */
    useEffect(() => {
        fetchCategories();
        fetchSubcategories();
    }, []);

    const fetchCategories = async () => {
        try {
            const res = await axios.get(API_CATEGORIES);
            setCategories(res.data);
        } catch (err) {
            console.error('Error fetching categories:', err);
        }
    };

    const fetchSubcategories = async () => {
        try {
            const res = await axios.get(API_SUBCATEGORIES);
            setSubcategories(res.data);
        } catch (err) {
            console.error('Error fetching subcategories:', err);
        }
    };

    /* ------------------------------------------------------------------ */
    /* --------------------------- ADD HANDLERS ------------------------- */
    /* ------------------------------------------------------------------ */
    const handleAddCategorySubmit = async (e) => {
        e.preventDefault();
        const payload = {
            CategoryName: newCatName,
            Description: newCatDescription,
            Type: newCatType,
            BudgetAmount: newCatBudget ? parseFloat(newCatBudget) : null,
        };
        try {
            await axios.post(API_CATEGORIES, payload);
            setShowAddCategoryModal(false);
            fetchCategories();
        } catch (err) {
            console.error('Error creating category:', err);
        }
    };

    const handleAddSubcatSubmit = async (e) => {
        e.preventDefault();
        const payload = {
            SubcategoryName: newSubcatName,
            CategoryID: parseInt(newSubcatCategoryId, 10),
            Type: newSubcatType,
            BudgetAmount: newSubcatBudget ? parseFloat(newSubcatBudget) : null,
        };
        try {
            await axios.post(API_SUBCATEGORIES, payload);
            setShowAddSubcatModal(false);
            fetchSubcategories();
        } catch (err) {
            console.error('Error creating subcategory:', err);
        }
    };

    /* ------------------------------------------------------------------ */
    /* --------------------------- EDIT HANDLERS ------------------------ */
    /* ------------------------------------------------------------------ */
    const handleEditCategory = (row) => setEditingCategory({ ...row });
    const handleEditSubcategory = (row) => setEditingSubcategory({ ...row });

    const handleSaveCategory = async () => {
        if (!editingCategory) return;
        const payload = {
            CategoryName: editingCategory.CategoryName,
            Description: editingCategory.Description,
            Type: editingCategory.Type,
            BudgetAmount: editingCategory.BudgetAmount ?? null,
        };
        try {
            await axios.put(`${API_CATEGORIES}/${editingCategory.apiid}`, payload);
            setEditingCategory(null);
            fetchCategories();
        } catch (err) {
            console.error('Error updating category:', err);
        }
    };

    const handleSaveSubcategory = async () => {
        if (!editingSubcategory) return;
        const payload = {
            SubcategoryName: editingSubcategory.SubcategoryName,
            CategoryID: editingSubcategory.CategoryID,
            Type: editingSubcategory.Type,
            BudgetAmount: editingSubcategory.BudgetAmount ?? null,
        };
        try {
            await axios.put(`${API_SUBCATEGORIES}/${editingSubcategory.apiid}`, payload);
            setEditingSubcategory(null);
            fetchSubcategories();
        } catch (err) {
            console.error('Error updating subcategory:', err);
        }
    };

    /* ------------------------------------------------------------------ */
    /* -------------------- APPLY / CLEAR BUDGETS ----------------------- */
    /* ------------------------------------------------------------------ */
    const handleApplyViewTotals = async () => {
        try {
            const { data: rows } = await axios.get(API_VIEW);
            setProgressTotal(rows.length);
            setProgressCount(0);
            setProgressOpen(true);

            for (let i = 0; i < rows.length; i++) {
                const row = rows[i];
                const totalNum = parseFloat(row.TotalBudget.replace(/[$,]/g, '')) || 0;

                if (row.SubCategoryId == null) {
                    const cat = categories.find(c => c.CategoryID === row.CategoryId);
                    if (cat) await axios.put(`${API_CATEGORIES}/${cat.apiid}`, { BudgetAmount: totalNum });
                } else {
                    const sub = subcategories.find(s => s.SubcategoryID === row.SubCategoryId);
                    if (sub) await axios.put(`${API_SUBCATEGORIES}/${sub.apiid}`, { BudgetAmount: totalNum });
                }
                setProgressCount(i + 1);
            }
            setTimeout(() => setProgressOpen(false), 500);
            alert('Applied view totals.');
            fetchCategories();
            fetchSubcategories();
        } catch (err) {
            console.error('Error applying view totals:', err);
            setProgressOpen(false);
            alert('Error applying view totals.');
        }
    };

    const handleClearAllBudgets = async () => {
        try {
            const totalOps = categories.length + subcategories.length;
            setProgressTotal(totalOps);
            setProgressCount(0);
            setProgressOpen(true);

            for (const cat of categories) {
                await axios.put(`${API_CATEGORIES}/${cat.apiid}`, { BudgetAmount: null });
                setProgressCount(prev => prev + 1);
            }
            for (const sub of subcategories) {
                await axios.put(`${API_SUBCATEGORIES}/${sub.apiid}`, { BudgetAmount: null });
                setProgressCount(prev => prev + 1);
            }
            setTimeout(() => setProgressOpen(false), 500);
            alert('Cleared all budgets.');
            fetchCategories();
            fetchSubcategories();
        } catch (err) {
            console.error('Error clearing budgets:', err);
            setProgressOpen(false);
            alert('Error clearing budgets.');
        }
    };

    /* ------------------------------------------------------------------ */
    /* ---------------------- GRID COLUMN DEFINITIONS ------------------- */
    /* ------------------------------------------------------------------ */
    const categoryColumns = [
        { field: 'CategoryID', headerName: 'ID', width: 70 },
        { field: 'CategoryName', headerName: 'Name', width: 200 },
        { field: 'Description', headerName: 'Description', width: 250 },
        { field: 'Type', headerName: 'Type', width: 120 },
        {
            field: 'BudgetAmount',
            headerName: 'Budget Amount',
            width: 150,
            valueFormatter: ({ value }) =>
                value != null
                    ? `$${new Intl.NumberFormat('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    }).format(value)}`
                    : '',
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 100,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Tooltip title="Edit row">
                    <IconButton onClick={() => handleEditCategory(params.row)}>
                        <EditIcon sx={{ color: '#EFFFFD' }} />
                    </IconButton>
                </Tooltip>
            ),
        },
    ];

    const subcategoryColumns = [
        { field: 'SubcategoryID', headerName: 'ID', width: 70 },
        { field: 'SubcategoryName', headerName: 'Name', width: 200 },
        {
            field: 'CategoryID',
            headerName: 'Main Category',
            width: 150,
            type: 'singleSelect',
            valueOptions: categories.map(cat => ({
                value: cat.CategoryID,
                label: cat.CategoryName,
            })),
        },
        { field: 'Type', headerName: 'Type', width: 120 },
        {
            field: 'BudgetAmount',
            headerName: 'Budget Amount',
            width: 150,
            valueFormatter: ({ value }) =>
                value != null
                    ? `$${new Intl.NumberFormat('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    }).format(value)}`
                    : '',
        },
        { field: 'BudgetString', headerName: 'Budget String', width: 200 },
        { field: 'FCT', headerName: 'FCT', width: 100 },
        { field: 'OBJ', headerName: 'OBJ', width: 100 },
        { field: 'LOCATION', headerName: 'Location', width: 120 },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 100,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Tooltip title="Edit row">
                    <IconButton onClick={() => handleEditSubcategory(params.row)}>
                        <EditIcon sx={{ color: '#EFFFFD' }} />
                    </IconButton>
                </Tooltip>
            ),
        },
    ];

    /* ------------------------------------------------------------------ */
    /* ------------------------------ RENDER ---------------------------- */
    /* ------------------------------------------------------------------ */
    return (
        <Box p={2} sx={{ backgroundColor: bgDefault, minHeight: '100vh' }}>
            {/* ------------------- PROGRESS MODAL ------------------- */}
            <Modal open={progressOpen}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 320,
                        bgcolor: bgPaper,
                        borderRadius: 2,
                        p: 3,
                        boxShadow: `0 0 20px rgba(0,0,0,0.5)`,
                        textAlign: 'center',
                    }}
                >
                    <Typography variant="h6" mb={2}>
                        Updating Budgets...
                    </Typography>
                    <LinearProgress
                        variant="determinate"
                        value={progressTotal ? (progressCount / progressTotal) * 100 : 0}
                        sx={{
                            height: 12,
                            borderRadius: 6,
                            backgroundColor: bgPaper,
                            '& .MuiLinearProgress-bar': { backgroundColor: successMain },
                        }}
                    />
                    <Typography variant="body2" mt={1}>
                        {progressCount} of {progressTotal}
                    </Typography>
                </Box>
            </Modal>

            {/* -------------------- TOP BUTTONS --------------------- */}
            <Box display="flex" justifyContent="flex-end" gap={2} mb={2}>
                <Button variant="contained" color="success" onClick={handleApplyViewTotals}>
                    Apply Finalized Budget Totals
                </Button>
                <Button variant="contained" color="error" onClick={handleClearAllBudgets}>
                    Clear All Budgets
                </Button>
            </Box>

            <Typography variant="h4" gutterBottom sx={{ color: textPrimary }}>
                Manage Finance Categories &amp; Subcategories
            </Typography>

            {/* -------------------- CATEGORIES ---------------------- */}
            <Box mb={4}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h5" sx={{ color: textPrimary }}>
                        Categories
                    </Typography>
                    <Button variant="contained" onClick={() => setShowAddCategoryModal(true)}>
                        Add Category
                    </Button>
                </Box>
                <Paper sx={{ p: 2, backgroundColor: headerBackground, mt: 2 }}>
                    <Box
                        sx={{
                            height: 550,
                            '& .MuiDataGrid-columnHeaders': { backgroundColor: actionHover, color: textPrimary },
                            '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: tableBackground },
                            '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: tableBackground },
                            '& .MuiDataGrid-cell': { color: textPrimary },
                        }}
                    >
                        <DataGrid
                            rows={categories}
                            columns={categoryColumns}
                            getRowId={(row) => row.apiid}
                            pageSize={20}
                            rowsPerPageOptions={[20, 50, 100]}
                            disableSelectionOnClick
                        />
                    </Box>
                </Paper>
            </Box>

            {/* -------------------- SUBCATEGORIES ------------------- */}
            <Box mb={4}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h5" sx={{ color: textPrimary }}>
                        Subcategories
                    </Typography>
                    <Button variant="contained" onClick={() => setShowAddSubcatModal(true)}>
                        Add Subcategory
                    </Button>
                </Box>
                <Paper sx={{ p: 2, backgroundColor: headerBackground, mt: 2 }}>
                    <Box
                        sx={{
                            height: 550,
                            '& .MuiDataGrid-columnHeaders': { backgroundColor: actionHover, color: textPrimary },
                            '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: tableBackground },
                            '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: tableBackground },
                            '& .MuiDataGrid-cell': { color: textPrimary },
                        }}
                    >
                        <DataGrid
                            rows={subcategories}
                            columns={subcategoryColumns}
                            getRowId={(row) => row.apiid}
                            pageSize={20}
                            rowsPerPageOptions={[20, 50, 100]}
                            disableSelectionOnClick
                        />
                    </Box>
                </Paper>
            </Box>

            {/* ------------ MODAL : ADD CATEGORY ------------ */}
            <Modal open={showAddCategoryModal} onClose={() => setShowAddCategoryModal(false)}>
                <Box
                    sx={{
                        width: 400,
                        bgcolor: '#16213E',
                        p: 3,
                        borderRadius: 2,
                        boxShadow: 24,
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        color: '#EFFFFD',
                    }}
                >
                    <Typography variant="h6" gutterBottom>
                        Create New Category
                    </Typography>
                    <form onSubmit={handleAddCategorySubmit}>
                        <TextField
                            label="Category Name"
                            value={newCatName}
                            onChange={(e) => setNewCatName(e.target.value)}
                            fullWidth
                            required
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            label="Description"
                            value={newCatDescription}
                            onChange={(e) => setNewCatDescription(e.target.value)}
                            fullWidth
                            multiline
                            rows={2}
                            sx={{ mb: 2 }}
                        />
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Type</InputLabel>
                            <Select
                                value={newCatType}
                                label="Type"
                                onChange={(e) => setNewCatType(e.target.value)}
                                required
                            >
                                <MenuItem value="Income">Income</MenuItem>
                                <MenuItem value="Expense">Expense</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label="Budget Amount"
                            type="number"
                            value={newCatBudget}
                            onChange={(e) => setNewCatBudget(e.target.value)}
                            fullWidth
                            sx={{ mb: 2 }}
                        />
                        <Button type="submit" variant="contained">
                            Save Category
                        </Button>
                    </form>
                </Box>
            </Modal>

            {/* ------------ MODAL : ADD SUBCATEGORY ---------- */}
            <Modal open={showAddSubcatModal} onClose={() => setShowAddSubcatModal(false)}>
                <Box
                    sx={{
                        width: 400,
                        bgcolor: '#16213E',
                        p: 3,
                        borderRadius: 2,
                        boxShadow: 24,
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        color: '#EFFFFD',
                    }}
                >
                    <Typography variant="h6" gutterBottom>
                        Create New Subcategory
                    </Typography>
                    <form onSubmit={handleAddSubcatSubmit}>
                        <TextField
                            label="Subcategory Name"
                            value={newSubcatName}
                            onChange={(e) => setNewSubcatName(e.target.value)}
                            fullWidth
                            required
                            sx={{ mb: 2 }}
                        />
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Category</InputLabel>
                            <Select
                                value={newSubcatCategoryId}
                                label="Category"
                                onChange={(e) => setNewSubcatCategoryId(e.target.value)}
                                required
                            >
                                {categories.map(cat => (
                                    <MenuItem key={cat.CategoryID} value={cat.CategoryID}>
                                        {cat.CategoryName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Type</InputLabel>
                            <Select
                                value={newSubcatType}
                                label="Type"
                                onChange={(e) => setNewSubcatType(e.target.value)}
                                required
                            >
                                <MenuItem value="Income">Income</MenuItem>
                                <MenuItem value="Expense">Expense</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label="Budget Amount"
                            type="number"
                            value={newSubcatBudget}
                            onChange={(e) => setNewSubcatBudget(e.target.value)}
                            fullWidth
                            sx={{ mb: 2 }}
                        />
                        <Button type="submit" variant="contained">
                            Save Subcategory
                        </Button>
                    </form>
                </Box>
            </Modal>

            {/* ------------ MODAL : EDIT CATEGORY ----------- */}
            <Modal open={!!editingCategory} onClose={() => setEditingCategory(null)}>
                <Box
                    sx={{
                        width: 400,
                        bgcolor: '#16213E',
                        p: 3,
                        borderRadius: 2,
                        boxShadow: 24,
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        color: '#EFFFFD',
                    }}
                >
                    <Typography variant="h6" gutterBottom>
                        Edit Category
                    </Typography>
                    <TextField
                        label="Category Name"
                        value={editingCategory?.CategoryName || ''}
                        onChange={(e) =>
                            setEditingCategory(prev => ({ ...prev, CategoryName: e.target.value }))
                        }
                        fullWidth
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label="Description"
                        value={editingCategory?.Description || ''}
                        onChange={(e) =>
                            setEditingCategory(prev => ({ ...prev, Description: e.target.value }))
                        }
                        fullWidth
                        multiline
                        rows={2}
                        sx={{ mb: 2 }}
                    />
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Type</InputLabel>
                        <Select
                            value={editingCategory?.Type || ''}
                            onChange={(e) =>
                                setEditingCategory(prev => ({ ...prev, Type: e.target.value }))
                            }
                        >
                            <MenuItem value="Income">Income</MenuItem>
                            <MenuItem value="Expense">Expense</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        label="Budget Amount"
                        type="number"
                        value={editingCategory?.BudgetAmount ?? ''}
                        onChange={(e) =>
                            setEditingCategory(prev => ({
                                ...prev,
                                BudgetAmount: parseFloat(e.target.value) || 0,
                            }))
                        }
                        fullWidth
                        sx={{ mb: 2 }}
                    />
                    <Box display="flex" justifyContent="flex-end" gap={1}>
                        <Button onClick={() => setEditingCategory(null)} variant="outlined">
                            Cancel
                        </Button>
                        <Button onClick={handleSaveCategory} variant="contained" color="success">
                            Save
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* ------------ MODAL : EDIT SUBCATEGORY -------- */}
            <Modal open={!!editingSubcategory} onClose={() => setEditingSubcategory(null)}>
                <Box
                    sx={{
                        width: 400,
                        bgcolor: '#16213E',
                        p: 3,
                        borderRadius: 2,
                        boxShadow: 24,
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        color: '#EFFFFD',
                    }}
                >
                    <Typography variant="h6" gutterBottom>
                        Edit Subcategory
                    </Typography>
                    <TextField
                        label="Subcategory Name"
                        value={editingSubcategory?.SubcategoryName || ''}
                        onChange={(e) =>
                            setEditingSubcategory(prev => ({ ...prev, SubcategoryName: e.target.value }))
                        }
                        fullWidth
                        sx={{ mb: 2 }}
                    />
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Type</InputLabel>
                        <Select
                            value={editingSubcategory?.Type || ''}
                            onChange={(e) =>
                                setEditingSubcategory(prev => ({ ...prev, Type: e.target.value }))
                            }
                        >
                            <MenuItem value="Income">Income</MenuItem>
                            <MenuItem value="Expense">Expense</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        label="Budget Amount"
                        type="number"
                        value={editingSubcategory?.BudgetAmount ?? ''}
                        onChange={(e) =>
                            setEditingSubcategory(prev => ({
                                ...prev,
                                BudgetAmount: parseFloat(e.target.value) || 0,
                            }))
                        }
                        fullWidth
                        sx={{ mb: 2 }}
                    />
                    <Box display="flex" justifyContent="flex-end" gap={1}>
                        <Button onClick={() => setEditingSubcategory(null)} variant="outlined">
                            Cancel
                        </Button>
                        <Button onClick={handleSaveSubcategory} variant="contained" color="success">
                            Save
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
};

export default CategoryManager;
